import { NGROK } from "../../../../APIs";
import { apiClient } from "../../../../utils/apiClient";
import { Resource } from "../interface";

export const getUsers = async (tenantName: string, deviceId: string, email: string) => {
    let url = `${NGROK}/api/adcomputer/users/${tenantName}/published/computer-user?computerId=${deviceId}&email=${email}`;
    const data = await apiClient({
        method: "GET",
        url: url
    });

    return data;
};

export const isLocalWindowsUser = async (
    tenantName: string,
    computerId: string | number,
    email: string,
): Promise<{
    showJitTab: boolean;
    cnAvailableOnMachine: boolean;
    applicationCreated: boolean;
}> => {
    const url = `${NGROK}/api/access/windows/is-local-user?tenantName=${encodeURIComponent(tenantName)}&computerId=${computerId}&email=${encodeURIComponent(email)}`;
    const data = await apiClient({
        method: "GET",
        url,
    });
    return {
        showJitTab: Boolean(data?.showJitTab),
        cnAvailableOnMachine: Boolean(data?.cnAvailableOnMachine),
        applicationCreated: Boolean(data?.applicationCreated),
    };
};

export const getAllWindowsCredentials = async (tenantName: string, deviceId: string) => {
    let url = `${NGROK}/api/${tenantName}/credentials?computerId=${deviceId}`;
    const data = await apiClient({
        method: "GET",
        url: url
    });
    return data;
}


export const submitForm = async (payload: any) => {
    let url = `${NGROK}/api/access/create`;
    const data = await apiClient({
        method: "POST",
        url: url,
        data: payload
    });

    return data;
};

export const fetchResources = async (
    tabType: string,
    tenantName: string,
    computerId: number | string,
    userId: number | string,
    searchValue: string = "",
    selectedResourceValue: "ALL" | "ALLOWED" | "DENIED" | "TIMED" = "ALL",
): Promise<Resource[]> => {
    try {
        let url = "";

        switch (tabType) {
            case "folder":
                url = `${NGROK}/api/${tenantName}/computers/${computerId}/computer-users/${userId}/folders`;
                break;
            case "app":
                url = `${NGROK}/api/computer-user/get-all-apps?userId=${userId}&computerId=${computerId}`;
                break;
            case "privilege":
                url = `${NGROK}/api/${tenantName}/computer-user-groups/membership/${userId}`;
                break;
            case "url":
                url = `${NGROK}/api/${tenantName}/computers/${computerId}/computer-users/${userId}/url-control`;
                break;
            default:
                console.warn("Unknown tabType:", tabType);
                return [];
        }

        const response = await apiClient({ method: "GET", url });
        const rawData = response?.content || response;
        if (!rawData) return [];

        const trustLevel = {
            ALLOWED: "ENABLED",
            DENIED: "DISABLED",
            TIMED: "DYNAMIC",
        };
        const inheritedTrustLevel = { ...trustLevel };

        const resources: Resource[] = rawData.map((item: any) => {
            switch (tabType) {
                case "folder":
                    return {
                        id: item.folderId?.toString(),
                        name: item.path,
                        type: "FOLDER",
                        path: item.path,
                        status: item.groupStatus,
                        inheritedGroupStatus: item.inheritedGroupStatus,
                        timedAccess: {
                            expiresAt: item.accessTimeOverAt ? new Date(item.accessTimeOverAt) : null,
                            durationSeconds: item.remainingTime ?? 0,
                        },
                        applicationReputation: item.applicationReputation,
                    };

                case "app":
                    const app = item.computerUsersPublishersAppsMembership;
                    return {
                        id: app.appId?.toString(),
                        name: app.appName,
                        type: "APP",
                        path: app.path,
                        status: app.groupStatus,
                        inheritedGroupStatus: app.inheritedGroupStatus,
                        publisher: app.publisherId,
                        timedAccess: {
                            expiresAt: app.accessTimeOverAt ? new Date(app.accessTimeOverAt) : null,
                            durationSeconds: app.remainingTime ?? 0,
                        },
                        applicationReputation: app.applicationReputation,
                    };

                case "privilege":
                    const group = item.adComputerUserGroup;
                    return {
                        id: group.id?.toString(),
                        name: group.cn,
                        type: "GROUP",
                        path: group.cn,
                        status: item.groupStatus,
                        inheritedGroupStatus: item.inheritedGroupStatus,
                        timedAccess: {
                            expiresAt: item.accessTimeOverAt ? new Date(item.accessTimeOverAt) : null,
                            durationSeconds: item.remainingTime ?? 0,
                        },
                        applicationReputation: item.applicationReputation,
                    };

                case "url":
                    return {
                        id: item.urlId?.toString(),
                        name: item.name,
                        type: "URL",
                        path: item.name,
                        status: item.groupStatus,
                        inheritedGroupStatus: item.inheritedGroupStatus,
                        timedAccess: {
                            expiresAt: item.accessTimeOverAt ? new Date(item.accessTimeOverAt) : null,
                            durationSeconds: item.remainingTime ?? 0,
                        },
                        applicationReputation: item?.applicationReputation,
                    };

                default:
                    return {} as Resource;
            }
        });


        const filterTimedResources = resources.filter((res) => {

            const desiredStatus = trustLevel["TIMED"];
            const desiredInherited = inheritedTrustLevel["TIMED"];

            if (res.status === "INHERITED") {
                return res.inheritedGroupStatus === desiredInherited;
            }
            return res.status === desiredStatus;
        });

        const filterDeniedResources = resources.filter((res) => {

            const desiredStatus = trustLevel["DENIED"];
            const desiredInherited = inheritedTrustLevel["DENIED"];

            if (res.status === "INHERITED") {
                return res.inheritedGroupStatus === desiredInherited;
            }
            return res.status === desiredStatus;
        });

        return [...filterTimedResources, ...filterDeniedResources];
    } catch (err) {
        console.error("fetchResources error:", err);
        return [];
    }
};


